import { SettingsIcon } from "@storytell/bsky-icons";
import { Features } from "@storytell/cp-client";
import { type Component, For, Show, createEffect, createSignal } from "solid-js";
import { usePromptBarContext } from "../PromptBarContext";
import { useTooltipBehavior, useViewportAwareTooltip } from "../hooks";
import { PromptBarButton } from "./PromptBarButton";

interface ModelOption {
  value: Features.Feature;
  label: string;
  category: string;
}

// Available models configuration
const AVAILABLE_MODELS: ModelOption[] = [
  // Storytell Models
  {
    value: Features.Feature.MODEL_STORYTELL_AUTO_SELECT,
    label: "Auto Select",
    category: "Storytell",
  },

  // Anthropic Models
  {
    value: Features.Feature.MODEL_ANTHROPIC_CLAUDE_37_SONNET,
    label: "Claude 3.7 Sonnet",
    category: "Anthropic",
  },
  {
    value: Features.Feature.MODEL_ANTHROPIC_CLAUDE_4_SONNET,
    label: "Claude 4 Sonnet",
    category: "Anthropic",
  },

  // OpenAI Models
  {
    value: Features.Feature.MODEL_OPENAI_GPT_41,
    label: "GPT-4.1",
    category: "OpenAI",
  },
  {
    value: Features.Feature.MODEL_OPENAI_GPT_41_MINI,
    label: "GPT-4.1 Mini",
    category: "OpenAI",
  },
  {
    value: Features.Feature.MODEL_OPENAI_03,
    label: "o3",
    category: "OpenAI",
  },

  // Google Models
  {
    value: Features.Feature.MODEL_GOOGLE_GEMINI_25_FLASH,
    label: "Gemini 2.5 Flash",
    category: "Google",
  },
  {
    value: Features.Feature.MODEL_GOOGLE_GEMINI_25_PRO,
    label: "Gemini 2.5 Pro",
    category: "Google",
  },
];

// Default model: Claude 3.7 Sonnet
const DEFAULT_MODEL = Features.Feature.MODEL_STORYTELL_AUTO_SELECT;

/**
 * Knowledge sources display component with settings icon and dropdown tooltip
 */
export const KnowledgeSourcesDisplay: Component = () => {
  const { knowledgeSources } = usePromptBarContext();
  const { handleMouseLeave, handleMouseEnter } = useTooltipBehavior(knowledgeSources.hideTooltip, 400);

  const [triggerRef, setTriggerRef] = createSignal<HTMLElement>();

  // Ensure Claude 3.7 is selected by default
  createEffect(() => {
    const currentModel = knowledgeSources.selectedModel();
    if (currentModel === undefined || currentModel === null) {
      knowledgeSources.setSelectedModel(DEFAULT_MODEL);
    }
  });

  // Viewport-aware tooltip positioning
  const { getTooltipClasses } = useViewportAwareTooltip(triggerRef, knowledgeSources.showTooltip, {
    tooltipWidth: 224, // 56 * 4 = 224px (w-56)
    tooltipHeight: 280, // Approximate height of the tooltip content
    margin: 16,
    preferredPosition: "bottom",
  });

  return (
    <div class="relative">
      <div ref={setTriggerRef}>
        <PromptBarButton
          noBorder
          onClick={knowledgeSources.toggleTooltip}
          icon={SettingsIcon}
          aria-label="Knowledge sources"
        />
      </div>

      {/* Knowledge Sources Tooltip */}
      <Show when={knowledgeSources.showTooltip()}>
        <div
          class={`${getTooltipClasses()} w-56 bg-canvas border border-primary-border rounded-lg shadow-xl overflow-hidden`}
          onMouseLeave={handleMouseLeave}
          onMouseEnter={handleMouseEnter}
        >
          {/*/!* Knowledge Source Toggles *!/*/}
          {/*<div class="p-3 space-y-2.5">*/}
          {/*  /!* Web Search *!/*/}
          {/*  <div class="flex items-center justify-between">*/}
          {/*    <span class="text-sm text-primary-text">World Knowledge</span>*/}
          {/*    <label class="relative inline-flex items-center cursor-pointer">*/}
          {/*      <input*/}
          {/*        type="checkbox"*/}
          {/*        class="sr-only peer"*/}
          {/*        checked={knowledgeSources.web()}*/}
          {/*        onChange={() => knowledgeSources.toggleWeb()}*/}
          {/*      />*/}
          {/*      <div class="w-7 h-4 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-3 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-blue-500" />*/}
          {/*    </label>*/}
          {/*  </div>*/}

          {/*  /!* Your Knowledge *!/*/}
          {/*  <div class="flex items-center justify-between">*/}
          {/*    <span class="text-sm text-primary-text">My Knowledge</span>*/}
          {/*    <label class="relative inline-flex items-center cursor-pointer">*/}
          {/*      <input*/}
          {/*        type="checkbox"*/}
          {/*        class="sr-only peer"*/}
          {/*        checked={knowledgeSources.myKnowledge()}*/}
          {/*        onChange={() => knowledgeSources.toggleMyKnowledge()}*/}
          {/*      />*/}
          {/*      <div class="w-7 h-4 bg-gray-300 dark:bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-3 peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:bg-blue-500" />*/}
          {/*    </label>*/}
          {/*  </div>*/}
          {/*</div>*/}

          {/* Model Selection */}
          <div class="px-3 pb-3">
            <div class="text-xs text-secondary-text mb-1.5 text-left pt-2">Model</div>
            <select
              class="w-full px-2.5 py-1.5 bg-tile border border-primary-border rounded text-sm text-primary-text focus:outline-none focus:ring-1 focus:ring-brand"
              value={String(knowledgeSources.selectedModel() ?? DEFAULT_MODEL)}
              onChange={(e) => knowledgeSources.setSelectedModel(Number(e.target.value) as Features.Feature)}
            >
              {/* Group models by category */}
              <optgroup label="Storytell">
                <For each={AVAILABLE_MODELS.filter((m) => m.category === "Storytell")}>
                  {(model) => <option value={model.value}>{model.label}</option>}
                </For>
              </optgroup>

              <optgroup label="Anthropic">
                <For each={AVAILABLE_MODELS.filter((m) => m.category === "Anthropic")}>
                  {(model) => <option value={model.value}>{model.label}</option>}
                </For>
              </optgroup>

              <optgroup label="OpenAI">
                <For each={AVAILABLE_MODELS.filter((m) => m.category === "OpenAI")}>
                  {(model) => <option value={model.value}>{model.label}</option>}
                </For>
              </optgroup>

              <optgroup label="Google">
                <For each={AVAILABLE_MODELS.filter((m) => m.category === "Google")}>
                  {(model) => <option value={model.value}>{model.label}</option>}
                </For>
              </optgroup>
            </select>
          </div>
        </div>
      </Show>
    </div>
  );
};
